import React, { useState, useEffect } from 'react';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  CircularProgress,
  Alert,
  Chip
} from '@mui/material';
import {
  TrendingUp,
  Devices,
  Apps,
  Security,
  CheckCircle,
  Speed,
  People
} from '@mui/icons-material';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, BarChart, Bar } from 'recharts';
import adminApi from '../services/api';
import { SystemStatsResponse, AnalyticsResponse, DeviceActivationAnalyticsResponse } from '../types/api';

interface DashboardData {
  systemStats: SystemStatsResponse;
  recentAnalytics: AnalyticsResponse;
  recentActivations: DeviceActivationAnalyticsResponse;
}

const Dashboard: React.FC = () => {
  const [data, setData] = useState<DashboardData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadDashboardData();
  }, []);

  const loadDashboardData = async () => {
    try {
      setLoading(true);
      setError(null);
      const dashboardData = await adminApi.getDashboardData();
      setData(dashboardData);
    } catch (err: unknown) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to load dashboard data';
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const formatUptime = (seconds: number): string => {
    const days = Math.floor(seconds / 86400);
    const hours = Math.floor((seconds % 86400) / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    
    if (days > 0) return `${days}d ${hours}h`;
    if (hours > 0) return `${hours}h ${minutes}m`;
    return `${minutes}m`;
  };

  const formatNumber = (num: number): string => {
    if (num >= 1000000) return `${(num / 1000000).toFixed(1)}M`;
    if (num >= 1000) return `${(num / 1000).toFixed(1)}K`;
    return num.toString();
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress size={60} />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ m: 2 }}>
        {error}
      </Alert>
    );
  }

  if (!data) return null;

  const { systemStats, recentAnalytics, recentActivations } = data;

  // Prepare chart data
  const requestsChartData = recentAnalytics.time_series.map(entry => ({
    date: new Date(entry.timestamp).toLocaleDateString(),
    successful: entry.successful_requests,
    failed: entry.failed_requests,
    total: entry.total_requests,
    successRate: (entry.success_rate * 100).toFixed(1)
  }));

  const activationsChartData = recentActivations.activation_time_series.map(entry => ({
    date: new Date(entry.timestamp).toLocaleDateString(),
    devices: entry.new_devices,
    phones: entry.new_phones,
    earbuds: entry.new_earbuds
  }));

  const environmentData = Object.entries(recentActivations.new_devices_by_environment).map(([env, count]) => ({
    name: env,
    value: count,
    color: env === 'production' ? '#2196f3' : '#ff9800'
  }));

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom sx={{ mb: 4, fontWeight: 'bold' }}>
        📊 Skywalk Licensing Dashboard
      </Typography>

      {/* System Health Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ height: '100%', background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', color: 'white' }}>
            <CardContent>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Box>
                  <Typography variant="h6" gutterBottom>Total Devices</Typography>
                  <Typography variant="h3" fontWeight="bold">{formatNumber(systemStats.total_devices)}</Typography>
                </Box>
                <Devices sx={{ fontSize: 48, opacity: 0.8 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ height: '100%', background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)', color: 'white' }}>
            <CardContent>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Box>
                  <Typography variant="h6" gutterBottom>Registered Apps</Typography>
                  <Typography variant="h3" fontWeight="bold">{systemStats.total_apps}</Typography>
                </Box>
                <Apps sx={{ fontSize: 48, opacity: 0.8 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ height: '100%', background: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)', color: 'white' }}>
            <CardContent>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Box>
                  <Typography variant="h6" gutterBottom>Requests Today</Typography>
                  <Typography variant="h3" fontWeight="bold">{formatNumber(systemStats.total_requests_today)}</Typography>
                </Box>
                <TrendingUp sx={{ fontSize: 48, opacity: 0.8 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ height: '100%', background: 'linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)', color: 'white' }}>
            <CardContent>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Box>
                  <Typography variant="h6" gutterBottom>Success Rate</Typography>
                  <Typography variant="h3" fontWeight="bold">
                    {((recentAnalytics.successful_requests / Math.max(recentAnalytics.total_requests, 1)) * 100).toFixed(1)}%
                  </Typography>
                </Box>
                <CheckCircle sx={{ fontSize: 48, opacity: 0.8 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* System Status */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Speed /> System Status
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <Typography variant="body2" color="text.secondary">Uptime</Typography>
                  <Typography variant="h6">{formatUptime(systemStats.uptime_seconds)}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2" color="text.secondary">Avg Response</Typography>
                  <Typography variant="h6">{systemStats.average_response_time_ms.toFixed(0)}ms</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2" color="text.secondary">Database</Typography>
                  <Chip 
                    label={systemStats.database_healthy ? 'Healthy' : 'Error'} 
                    color={systemStats.database_healthy ? 'success' : 'error'}
                    size="small"
                  />
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2" color="text.secondary">Redis</Typography>
                  <Chip 
                    label={systemStats.redis_healthy ? 'Healthy' : 'Error'} 
                    color={systemStats.redis_healthy ? 'success' : 'error'}
                    size="small"
                  />
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Security /> Security Metrics
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <Typography variant="body2" color="text.secondary">Attestation Rate (24h)</Typography>
                  <Typography variant="h6">{(systemStats.attestation_success_rate_24h * 100).toFixed(1)}%</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2" color="text.secondary">StrongBox Devices</Typography>
                  <Typography variant="h6">{systemStats.strongbox_devices}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2" color="text.secondary">Rate Limits Active</Typography>
                  <Typography variant="h6">{systemStats.active_rate_limits}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2" color="text.secondary">Total Key Requests</Typography>
                  <Typography variant="h6">{formatNumber(systemStats.total_key_requests)}</Typography>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Charts */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} lg={8}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>Authentication Requests (Last 7 Days)</Typography>
              <ResponsiveContainer width="100%" height={300}>
                <LineChart data={requestsChartData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="date" />
                  <YAxis />
                  <Tooltip />
                  <Line type="monotone" dataKey="successful" stroke="#4caf50" strokeWidth={2} name="Successful" />
                  <Line type="monotone" dataKey="failed" stroke="#f44336" strokeWidth={2} name="Failed" />
                </LineChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} lg={4}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>New Activations by Environment</Typography>
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie
                    data={environmentData}
                    cx="50%"
                    cy="50%"
                    outerRadius={80}
                    fill="#8884d8"
                    dataKey="value"
                    label={({ name, value }) => `${name}: ${value}`}
                  >
                    {environmentData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Device Activations */}
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <People /> Device Activations (Last 7 Days)
              </Typography>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={activationsChartData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="date" />
                  <YAxis />
                  <Tooltip />
                  <Bar dataKey="devices" fill="#2196f3" name="Device Pairs" />
                  <Bar dataKey="phones" fill="#ff9800" name="Phones" />
                  <Bar dataKey="earbuds" fill="#4caf50" name="Earbuds" />
                </BarChart>
              </ResponsiveContainer>
              
              <Box sx={{ mt: 2, display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                <Chip label={`${recentActivations.total_new_devices} New Devices`} color="primary" />
                <Chip label={`${recentActivations.activation_rate_per_day.toFixed(1)} per day`} color="secondary" />
                <Chip label={`${(recentActivations.phone_reuse_rate * 100).toFixed(1)}% Phone Reuse`} />
                <Chip label={`${(recentActivations.earbuds_reuse_rate * 100).toFixed(1)}% Earbuds Reuse`} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

export default Dashboard; 